import React, { useEffect, useState } from 'react'
import {
  ARTWORK_QUALITY_WARNING_COPY,
  OVERSIZED_ARTWORK_WARNING_COPY,
  SHEET_COVERAGE_WARNING_COPY,
  buildArtworkQualityWarnings,
  buildOversizedArtworkWarnings,
  isSheetMostlyEmpty,
} from './artworkQuality'
import { filterLayoutToIncludedSources } from './artworkSource'
import QuoteDetails from './QuoteDetails'
import BuilderAlert from './BuilderAlert'

export default function ReviewScreen({ layout, thumbnails, sheetSize, images = [], quantity = 1, pricingMode, quote, onQuantityChange, onEdit, onAddArtwork, onContinue }) {
  const includedLayout = filterLayoutToIncludedSources(layout, images)
  const { sheets } = includedLayout
  const oversizedArtworkWarnings = buildOversizedArtworkWarnings(includedLayout, images)
  const artworkQualityWarnings = buildArtworkQualityWarnings(includedLayout, images)
  const veryLowDpiWarnings = artworkQualityWarnings.filter(warning => warning.quality === 'poor')
  const cautionArtworkWarnings = artworkQualityWarnings.filter(warning => warning.quality === 'caution')
  const [artworkQualityAcknowledged, setArtworkQualityAcknowledged] = useState(false)
  const warningSignature = artworkQualityWarnings.map(warning => (
    `${warning.sheetNumber}:${warning.itemIndex}:${warning.dpi}:${warning.quality}`
  )).join('|')
  const showSheetCoverageWarning = isSheetMostlyEmpty(includedLayout)
  const hasVeryLowDpiArtwork = veryLowDpiWarnings.length > 0
  const artworkQualityAcknowledgementRequired = cautionArtworkWarnings.length > 0 && !hasVeryLowDpiArtwork

  useEffect(() => {
    setArtworkQualityAcknowledged(false)
  }, [warningSignature])

  const clampQuantity = (value) => Math.min(99, Math.max(1, value))

  const decrement = () => onQuantityChange?.(clampQuantity(quantity - 1))
  const increment = () => onQuantityChange?.(clampQuantity(quantity + 1))

  const handleQuantityChange = (e) => {
    const val = parseInt(e.target.value, 10)
    if (isNaN(val) || val < 1) {
      onQuantityChange?.(1)
    } else if (val > 99) {
      onQuantityChange?.(99)
    } else {
      onQuantityChange?.(val)
    }
  }

  return (
    <div className="review-screen">
      <h2 className="review-screen__title">Review Your Design</h2>
      <p className="review-screen__subtitle">
        {pricingMode === 'per_square_foot' ? 'Automatic-height sheet' : `${sheetSize.label} sheet`}
      </p>
      <p className="review-screen__preview-note">
        Preview is for layout; final file is generated at print resolution.
      </p>

      <section className="review-screen__design-summary" aria-label="Gang sheet preview">
        {pricingMode === 'per_square_foot' && (
          <QuoteDetails quote={quote} testId="review-pricing-quote" totalOnly />
        )}

        {oversizedArtworkWarnings.length > 0 && (
          <BuilderAlert className="review-screen__quality-warning" tone="error" title="Artwork is larger than the sheet" data-testid="review-sheet-size-warning">
            <p className="review-screen__quality-warning-copy">{OVERSIZED_ARTWORK_WARNING_COPY}</p>
            <ul className="review-screen__quality-warning-list">
              {oversizedArtworkWarnings.map(warning => (
                <li key={`${warning.sheetNumber}-${warning.itemIndex}-${warning.imageName}`}>
                  {warning.imageName} on sheet {warning.sheetNumber}
                </li>
              ))}
            </ul>
          </BuilderAlert>
        )}

        {artworkQualityWarnings.length > 0 && (
          <BuilderAlert
            className="review-screen__quality-warning"
            tone="warning"
            title="Low DPI detected"
            data-testid="review-quality-warning"
          >
            <p className="review-screen__quality-warning-copy">
              {ARTWORK_QUALITY_WARNING_COPY}
            </p>
            <ul className="review-screen__quality-warning-list">
              {artworkQualityWarnings.map(warning => (
                <li key={`${warning.sheetNumber}-${warning.itemIndex}-${warning.imageName}`}>
                  {warning.imageName} on sheet {warning.sheetNumber}: {warning.dpi} DPI
                </li>
              ))}
            </ul>
          </BuilderAlert>
        )}

        {showSheetCoverageWarning && (
          <aside className="review-screen__coverage-advice" aria-labelledby="review-coverage-advice-title" data-testid="review-sheet-coverage-warning">
            <p id="review-coverage-advice-title" className="review-screen__coverage-advice-title">Space-saving tip</p>
            <p className="review-screen__coverage-advice-copy">{SHEET_COVERAGE_WARNING_COPY}</p>
          </aside>
        )}

        <div className="review-screen__sheets">
          {sheets.map((sheet, i) => (
            <div
              key={sheet.sheet_number}
              className={`review-screen__sheet-card${sheet.height_in / sheet.width_in >= 2 ? ' review-screen__sheet-card--tall' : ''}`}
              data-testid={`review-sheet-${sheet.sheet_number}`}
            >
              {thumbnails[i] && (
                <img
                  src={thumbnails[i]}
                  alt={`Sheet ${sheet.sheet_number} preview`}
                  className="review-screen__thumbnail"
                  style={{ aspectRatio: `${sheet.width_in} / ${sheet.height_in}` }}
                />
              )}
              <div className="review-screen__sheet-info">
                <p className="review-screen__sheet-dims">
                  {sheet.width_in}″ × {sheet.height_in}″
                </p>
                <p className="review-screen__sheet-count">
                  {(sheet.items || []).length} image{(sheet.items || []).length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {pricingMode !== 'per_square_foot' && <div className="review-screen__quantity">
        <label className="review-screen__quantity-label" htmlFor="sheet-quantity">
          How many copies of this {sheets.length > 1 ? 'sheet set' : 'sheet'}?
          {sheets.length > 1 && <span> Each copy includes {sheets.length} physical sheets.</span>}
        </label>
        <div className="review-screen__quantity-stepper">
          <button
            type="button"
            className="review-screen__quantity-btn"
            onClick={decrement}
            disabled={quantity <= 1}
            aria-label="Decrease quantity"
          >
            -
          </button>
          <input
            id="sheet-quantity"
            className="review-screen__quantity-input"
            type="number"
            min="1"
            max="99"
            value={quantity}
            onFocus={event => event.currentTarget.select()}
            onChange={handleQuantityChange}
            data-testid="quantity-input"
          />
          <button
            type="button"
            className="review-screen__quantity-btn"
            onClick={increment}
            disabled={quantity >= 99}
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>
      </div>}

      {artworkQualityAcknowledgementRequired && (
        <label className="review-screen__quality-acknowledgement">
          <input
            type="checkbox"
            checked={artworkQualityAcknowledged}
            onChange={event => setArtworkQualityAcknowledged(event.target.checked)}
          />
          <span>
            I reviewed the spelling, details, edges, background, and transparency. GSPressHQ will print the artwork as shown.
          </span>
        </label>
      )}

      <div className="review-screen__actions">
        <div className="review-screen__action-option">
          <button className="review-screen__edit-btn" onClick={onEdit} aria-describedby="review-edit-layout-help">
            Edit layout
          </button>
          <p id="review-edit-layout-help" className="review-screen__action-help">Move or resize placed artwork.</p>
        </div>
        {onAddArtwork && (
          <div className="review-screen__action-option">
            <button className="review-screen__edit-btn" onClick={onAddArtwork} aria-describedby="review-add-artwork-help">
              Add more artwork
            </button>
            <p id="review-add-artwork-help" className="review-screen__action-help">Upload designs or change sizes and quantities.</p>
          </div>
        )}
        <button
          className="review-screen__continue-btn"
          onClick={() => onContinue(hasVeryLowDpiArtwork || artworkQualityAcknowledged)}
          disabled={artworkQualityAcknowledgementRequired && !artworkQualityAcknowledged}
          title={artworkQualityAcknowledgementRequired && !artworkQualityAcknowledged
            ? 'Review and acknowledge the artwork quality warning before continuing'
            : undefined}
        >
          {hasVeryLowDpiArtwork
            ? 'Continue at my own risk →'
            : pricingMode === 'per_square_foot' ? 'Review quote →' : 'Continue →'}
        </button>
      </div>
    </div>
  )
}
